import React, { useEffect, useRef } from "react";
import {
  BadgeCheck,
  Landmark,
  DraftingCompass,
  GitBranch,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { IMAGES } from "@/lib/site";
import { trackSpotlight } from "@/lib/spotlight";
import { ScrollCheck } from "@/components/ScrollCheck";

const REASONS = [
  {
    icon: BadgeCheck,
    title: "Deep Core Banking experience",
    text: "Our consultants live and breathe core banking. From legacy releases to the latest platforms, we know the product, the upgrade paths and the pitfalls — so you don't discover them the hard way.",
  },
  {
    icon: Landmark,
    title: "Genuine financial-services domain understanding",
    text: "We speak the language of banking — accounting, payments, lending, regulatory reporting and risk. That context turns technical delivery into real business outcomes.",
  },
  {
    icon: DraftingCompass,
    title: "Architecture-led delivery",
    text: "Every engagement starts with sound architecture. We design for resilience, performance and maintainability before a single line of configuration changes.",
  },
  {
    icon: GitBranch,
    title: "Migration & modernization capability",
    text: "Upgrades, re-platforming and cloud modernization are core to what we do. Our accelerators and automation de-risk even the most complex transitions.",
  },
  {
    icon: ShieldCheck,
    title: "Reliability, governance & QA",
    text: "Banking-grade governance, automated regression and rigorous release management protect every go-live and keep auditors and regulators comfortable.",
  },
  {
    icon: Clock,
    title: "Always-on managed support",
    text: "SLA-backed support across time zones keeps your core healthy long after launch, with proactive monitoring and continuous optimisation.",
  },
];

const COMPARE = [
  "Core-banking-only focus, not a generalist SI",
  "Senior, hands-on consultants — no bait-and-switch",
  "Reusable accelerators that compress timelines",
  "Transparent governance and reporting",
  "Fixed-scope or managed-capacity engagement models",
  "Knowledge transfer baked into every project",
];

const WhyUs = () => {
  const reasonsGrid = useRef(null);
  const reasonsBand = useRef(null);

  useEffect(() => {
    const grid = reasonsGrid.current;
    const updateInsets = () => {
      // Use card heights, not viewport units, to preserve the overlap at every breakpoint.
      reasonsBand.current.style.top = `${grid.firstElementChild.offsetHeight * 0.2}px`;
      reasonsBand.current.style.bottom = `${grid.lastElementChild.offsetHeight * 0.2}px`;
    };
    updateInsets();
    const observer = new ResizeObserver(updateInsets);
    observer.observe(grid);
    observer.observe(grid.firstElementChild);
    observer.observe(grid.lastElementChild);
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="why-us-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">Why Sphere IT Solutions</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
              The trusted choice for Core Banking programmes.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              Banks choose Sphere IT Solutions when the stakes are high and the margin for error is
              low. Here is what sets our delivery apart.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <img
              src="/media/why_us_intro_optimized.avif"
              alt="3D glass shield representing trust, security and governance"
              className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl ring-1 ring-slate-200"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      {/* REASONS GRID */}
      <section className="py-20 md:py-28">
        <div className="relative">
          <div ref={reasonsBand} data-testid="reasons-background" className="absolute inset-x-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <img src={`${process.env.PUBLIC_URL}/media/Contact_background_1.png`} alt="" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-white/40" />
          </div>
          <div ref={reasonsGrid} data-testid="reasons-grid" className="relative mx-auto max-w-7xl px-6 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.06}>
                <div onPointerMove={trackSpotlight} className="raised-surface service-spotlight rounded-2xl border border-slate-200 bg-white p-8 h-full hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all duration-300">
                  <div className="gold-icon-tile w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md">
                    <r.icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-900 font-heading">{r.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON / DIFFERENTIATORS */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-x-16 lg:gap-y-0 items-stretch" data-testid="difference-layout">
          <Reveal className="lg:row-span-3 lg:grid lg:grid-rows-subgrid">
            <div>
              <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">The difference</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Specialists, not generalists
              </h2>
            </div>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed lg:self-start">
              We don't try to be everything to everyone. Our singular focus on core banking
              means deeper expertise, fewer surprises and faster, more reliable delivery.
            </p>
            <div className="mt-8 relative lg:self-end">
              <img
                src= "/media/why_difference_intro.avif"
                alt="Specialists collaborating at a whiteboard on strategic delivery planning"
                className="relative rounded-3xl w-full aspect-[16/9] object-cover shadow-xl ring-1 ring-slate-200"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-start-2 lg:row-start-2 lg:row-span-2 lg:pt-1">
            <ul className="flex h-full flex-col justify-between gap-4" data-testid="difference-points">
              {COMPARE.map((item) => (
                <li key={item} onPointerMove={trackSpotlight} className="gold-check-row raised-surface service-spotlight flex items-start gap-3 rounded-xl bg-white border border-slate-200 px-5 py-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                  <ScrollCheck className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default WhyUs;
